import { MainApiService, Table, useRole, useConfig, useFetch, useNotificationContext } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import GroupIcon from "@mui/icons-material/Group";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { GlobalAxios } from "../../../axios/axios-config";
import { IIndividual } from "../../../models/individuals";
import { CPFMask } from "../../../utils/maks";
import { useInstalledExtensions } from "../../../hooks/useInstalledExtensions";
import { syncBeneficiary } from "../../../services/individualBeneficiaries.service";
import { getEstToken } from "../../../services/establishment.service";

const INDIVIDUALS_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Individual/";
const TRANSLATION_PATH = "page.register.individuals.";

const genericService = new MainApiService<IIndividual>(
  INDIVIDUALS_ROUTE,
  GlobalAxios
);

const IndividualsPage = () => {
  const navigate = useNavigate();
  const createPermission = useRole(["Create"]);

  const { t } = useTranslation();
  const { setMessage } = useNotificationContext();

  const columns: GridColDef<IIndividual>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t(TRANSLATION_PATH + "name") || "Name",
      },
      {
        field: "document",
        headerName: t(TRANSLATION_PATH + "document") || "Document",
        renderCell: (params) => {
          return params.value ? CPFMask.resolve(params.value) : "";
        },
      },
      {
        field: "birthDate",
        headerName: t(TRANSLATION_PATH + "birth_date") || "Birth date",
        renderCell: (params) => {
          if (params.value) {
            const dateParts = params.value.split("-");
            const newDate =
              dateParts[2] + "/" + dateParts[1] + "/" + dateParts[0];
            return newDate;
          }
          return "-";
        },
      },
    ],
    [t]
  );

  const handleRowClick = (params: GridRowParams<IIndividual>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
  };

  const importsPeopleHandler = () => {
    navigate("imports");
  };

  const [hasBeneficiarySyncExtension] = useInstalledExtensions("26");

  const {
    establishments,
    configuration: {
      establishment: { id: defaultEstablishmentId },
    },
  } = useConfig();

  const { eI } = useParams();

  /**Estabelecimento da aba, derivado da url. Usado quando o token não possui o
   * id do estabelecimento gravado
   */
  const actualEstablishmentId = useMemo(
    () => establishments.find((e) => e.tenantSequential === Number(eI))?.id,
    [establishments, eI]
  );

  const { sendRequest: syncBeneficiaryRequest, loading: syncLoading } =
    useFetch(syncBeneficiary);

  const onSyncBeneficiary = useCallback(
    async (document: string) => {
      if (!document) {
        setMessage({
          message: `${t(TRANSLATION_PATH + "beneficiary_sync_no_document")}`,
          type: "error",
          notificationKey: new Date().getMilliseconds(),
        });
        return;
      }

      const { success } = await syncBeneficiaryRequest({
        establishmentId:
          getEstToken()?.establishmentId ||
          actualEstablishmentId ||
          defaultEstablishmentId,
        document,
      });

      if (success) {
        setMessage({
          message: `${t(TRANSLATION_PATH + "beneficiary_sync_success")}`,
          type: "success",
          notificationKey: new Date().getMilliseconds(),
        });
      }
    },
    [
      setMessage,
      syncBeneficiaryRequest,
      actualEstablishmentId,
      defaultEstablishmentId,
      t,
    ]
  );

  return (
    <Table<IIndividual>
      showDefaultMacroFilters={false}
      service={genericService}
      columns={columns}
      title={t(TRANSLATION_PATH + "individuals") || "Individuals"}
      searchInput
      onAdd={handleAdd}
      showButton={createPermission}
      showDeleteButton={false}
      syncing={syncLoading}
      onRowClick={handleRowClick}
      tableActionsMenu={[
        {
          icon: <GroupIcon />,
          text: t(`${TRANSLATION_PATH}imports`),
          action: importsPeopleHandler,
        },
      ]}
      searchInputPlaceHolder={
        t(TRANSLATION_PATH + "search_individuals") || "Searh for an individual"
      }
      addButtonLabel={
        t(TRANSLATION_PATH + "add_individual") || "Add Individual"
      }
      rowActions={() =>
        hasBeneficiarySyncExtension
          ? [
              {
                action: (params) => {
                  onSyncBeneficiary(params.row.document);
                },
                icon: <SyncRoundedIcon fontSize="small" />,
                text: `${t(TRANSLATION_PATH + "sync_beneficiary.button")}`,
              },
            ]
          : []
      }
      confirmOptions={{
        title: t(TRANSLATION_PATH + "confirm.title"),
        description: t(TRANSLATION_PATH + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default IndividualsPage;
